import { useEffect, useState, type ComponentProps } from "react"
import { CheckIcon, CopyIcon } from "@phosphor-icons/react"
import { cva, type VariantProps } from "class-variance-authority"
import { toast } from "react-toastify"

import { IconButton, type IconButtonProps } from "~/components/design-system/components"
import { cn } from "~/utils/helpers"
import { highlightLines } from "~/utils/highlighter"

/**
 * Code, JSON, SQL, terminal output and diffs, with optional header, line numbers and copy button.
 * Port of shadcn/ui `code-block` (radix-nova) onto Hakio tokens; highlighting via TanStack Highlight.
 * {@link ../design-system/intents.ts CODE_BLOCK_INTENT}
 */
const codeBlockVariants = cva(
  "group/code-block relative flex w-full min-w-0 flex-col overflow-hidden rounded-lg bg-subtle text-sm has-[>[data-slot=code-block-content]:focus-visible]:shadow-borders-interactive-with-shadow data-[size=sm]:text-xs",
  {
    variants: {
      variant: {
        default: "border border-base text-fg-base",
        destructive: "border border-badge-red-border text-fg-error-icon",
      },
    },
    defaultVariants: { variant: "default" },
  },
)

function CodeBlock({
  className,
  variant = "default",
  size = "default",
  ...props
}: ComponentProps<"div"> & VariantProps<typeof codeBlockVariants> & { size?: "default" | "sm" }) {
  return (
    <div
      data-slot="code-block"
      data-variant={variant}
      data-size={size}
      className={cn(codeBlockVariants({ variant }), className)}
      {...props}
    />
  )
}

function CodeBlockHeader({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="code-block-header"
      className={cn(
        "flex min-w-0 shrink-0 items-center gap-2 px-3 pt-3 text-fg-muted group-data-[size=sm]/code-block:px-2.5 group-data-[size=sm]/code-block:pt-2.5 group-data-[variant=destructive]/code-block:text-fg-error-icon *:has-[>[data-slot=code-block-title]]:min-w-0",
        className,
      )}
      {...props}
    />
  )
}

function CodeBlockTitle({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="code-block-title"
      className={cn(
        "min-w-0 truncate font-sans text-sm font-medium text-fg-base group-data-[size=sm]/code-block:text-xs group-data-[variant=destructive]/code-block:text-fg-error-icon [&_svg]:pointer-events-none [&_svg]:inline-block [&_svg]:align-middle [&_svg:not([class*='size-'])]:size-3.5 *:[svg]:me-1.5",
        className,
      )}
      {...props}
    />
  )
}

function CodeBlockActions({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="code-block-actions"
      className={cn(
        "-my-1 ms-auto -me-1.5 flex shrink-0 items-center gap-1 [[data-slot=code-block]>&]:absolute [[data-slot=code-block]>&]:end-2 [[data-slot=code-block]>&]:top-2 [[data-slot=code-block]>&]:z-10 [[data-slot=code-block]>&]:m-0 [[data-slot=code-block]>&]:opacity-0 [[data-slot=code-block]>&]:transition-opacity [[data-slot=code-block]>&]:group-focus-within/code-block:opacity-100 [[data-slot=code-block]>&]:group-hover/code-block:opacity-100 group-data-[size=sm]/code-block:[[data-slot=code-block]>&]:end-1.5 group-data-[size=sm]/code-block:[[data-slot=code-block]>&]:top-1.5 [[data-slot=code-block]>&]:pointer-coarse:opacity-100",
        className,
      )}
      {...props}
    />
  )
}

function CodeBlockCopyButton({
  value,
  label = "Copy",
  variant = "neutral",
  size = "xs",
  onClick,
  ...props
}: Omit<IconButtonProps, "children" | "aria-label"> & { value: string; label?: string }) {
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!copied) return
    const timeout = setTimeout(() => setCopied(false), 2000)
    return () => clearTimeout(timeout)
  }, [copied])

  return (
    <>
      <IconButton
        data-slot="code-block-copy-button"
        data-copied={copied || undefined}
        variant={variant}
        size={size}
        aria-label={copied ? "Copied" : label}
        onClick={(event) => {
          onClick?.(event)
          navigator.clipboard
            .writeText(value)
            .then(() => setCopied(true))
            .catch(() => toast.error("Couldn't copy to the clipboard."))
        }}
        {...props}
      >
        {copied ? <CheckIcon /> : <CopyIcon />}
      </IconButton>
      <span aria-live="polite" className="sr-only">
        {copied ? "Copied" : ""}
      </span>
    </>
  )
}

function CodeBlockContent({
  className,
  children,
  style,
  wrap = false,
  lineNumbers = false,
  startLine = 1,
  ...props
}: ComponentProps<"pre"> & { wrap?: boolean; lineNumbers?: boolean; startLine?: number }) {
  return (
    <pre
      data-slot="code-block-content"
      data-wrap={wrap || undefined}
      data-numbered={lineNumbers || undefined}
      dir="ltr"
      tabIndex={0}
      style={{ counterReset: `line ${startLine - 1}`, ...style }}
      className={cn(
        "min-h-0 min-w-0 overflow-auto px-3 py-3 text-start font-mono leading-relaxed outline-hidden group-has-data-[slot=code-block-footer]/code-block:pb-2 group-has-data-[slot=code-block-header]/code-block:pt-2 group-data-[size=sm]/code-block:px-2.5 group-data-[size=sm]/code-block:py-2.5 group-data-[size=sm]/code-block:group-has-data-[slot=code-block-header]/code-block:pt-1.5 data-wrap:wrap-anywhere data-wrap:whitespace-pre-wrap",
        className,
      )}
      {...props}
    >
      <code
        data-slot="code-block-code"
        className="grid min-h-lh min-w-max content-start in-data-numbered:grid-cols-[auto_1fr] in-data-wrap:min-w-0"
      >
        {lineNumbers && typeof children === "string"
          ? children
              .replace(/\n$/, "")
              .split("\n")
              .map((line, index) => <CodeBlockLine key={index}>{line}</CodeBlockLine>)
          : children}
      </code>
    </pre>
  )
}

const codeBlockLineVariants = cva(
  "relative -mx-3 block min-h-lh px-3 group-data-[size=sm]/code-block:-mx-2.5 group-data-[size=sm]/code-block:px-2.5 before:min-w-6 before:text-end before:text-fg-muted after:absolute after:start-1 after:top-0 after:text-fg-muted after:select-none in-data-numbered:col-span-2 in-data-numbered:grid in-data-numbered:grid-cols-subgrid in-data-numbered:gap-x-3 in-data-numbered:[counter-increment:line] in-data-numbered:before:content-[counter(line)] in-data-numbered:before:select-none in-data-numbered:supports-[content:counter(line)_/_'']:before:content-[counter(line)_/_'']",
  {
    variants: {
      variant: {
        default: "",
        added: "bg-badge-green-bg after:content-['+']",
        removed: "bg-badge-red-bg after:text-fg-error-icon after:content-['−']",
        highlighted: "bg-medium",
      },
    },
    defaultVariants: { variant: "default" },
  },
)

function CodeBlockLine({
  className,
  variant = "default",
  children,
  ...props
}: ComponentProps<"span"> & VariantProps<typeof codeBlockLineVariants>) {
  // An empty line holds a newline so copying a selection keeps blank lines.
  const empty =
    children === undefined ||
    children === null ||
    children === "" ||
    (Array.isArray(children) && children.length === 0)

  return (
    <span
      data-slot="code-block-line"
      data-variant={variant}
      className={cn(codeBlockLineVariants({ variant }), className)}
      {...props}
    >
      <span data-slot="code-block-line-content" className="min-w-0">
        {empty ? "\n" : children}
      </span>
    </span>
  )
}

// Above this, tokenizing during render costs more than colours are worth.
const MAX_HIGHLIGHT_LENGTH = 50_000

/** `CodeBlockContent` with `code` highlighted for `language`. */
function CodeBlockCode({
  code,
  language,
  ...props
}: Omit<ComponentProps<typeof CodeBlockContent>, "children"> & {
  code: string
  language: string
}) {
  const text = code.replace(/\n$/, "")
  const lines =
    text.length <= MAX_HIGHLIGHT_LENGTH ? highlightLines(text, language) : text.split("\n")
  return (
    <CodeBlockContent {...props}>
      {lines.map((line, index) => (
        <CodeBlockLine key={index}>{line}</CodeBlockLine>
      ))}
    </CodeBlockContent>
  )
}

/** The common case: highlighted code with a copy button that appears on hover. */
function CopyableCode({
  code,
  language,
  copyLabel,
  size,
  wrap,
  className,
}: {
  code: string
  language: string
  copyLabel?: string
  size?: ComponentProps<typeof CodeBlock>["size"]
  wrap?: boolean
  className?: string
}) {
  return (
    <CodeBlock size={size} className={className}>
      <CodeBlockActions>
        <CodeBlockCopyButton value={code} label={copyLabel} size={size === "sm" ? "2xs" : "xs"} />
      </CodeBlockActions>
      <CodeBlockCode code={code} language={language} wrap={wrap} />
    </CodeBlock>
  )
}

function CodeBlockFooter({ className, ...props }: ComponentProps<"div">) {
  return (
    <div
      data-slot="code-block-footer"
      className={cn(
        "flex min-w-0 shrink-0 items-center gap-2 px-3 pb-3 text-fg-muted group-data-[size=sm]/code-block:px-2.5 group-data-[size=sm]/code-block:pb-2.5",
        className,
      )}
      {...props}
    />
  )
}

export {
  CodeBlock,
  CodeBlockActions,
  CodeBlockCode,
  CodeBlockContent,
  CodeBlockCopyButton,
  CodeBlockFooter,
  CodeBlockHeader,
  CodeBlockLine,
  CodeBlockTitle,
  CopyableCode,
}
